import { Badge, Banner, Button, Loader, Table, Text } from '@cloudflare/kumo'
import type {
  AdminPaymentLedgerView,
  Entitlements,
  PaymentGrantAction,
  PaymentStatus,
} from '@repo/shared'
import { useEffect, useState } from 'react'
import { request } from '../api'
import { useGetToken } from '../lib/auth'
import { fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'
import { PaymentReviewDialog } from './payment-review-dialog'

// 관리자 웹은 기존 운영 도구와 동일하게 한국어로 표시한다. 고객 문구가 아니다.
const STATUS: Record<PaymentStatus, string> = {
  pending: '결제 대기',
  paid: '결제',
  failed: '실패',
  cancelled: '취소',
  partially_refunded: '부분 환불',
  refunded: '환불',
}
const ACTION: Record<PaymentGrantAction, string> = {
  revoked: '미사용 권리 회수',
  partly_used: '남은 팩 횟수 회수·사용분 확인 필요',
  already_used: '사용한 견적권·자동 회수 없음',
  not_found: '지급 내역 확인 필요',
  partial_refund: '부분 환불·자동 회수 없음',
  revoke_off: '자동 회수 꺼짐',
  refund_reversed: '환불 취소·권리 복구 확인 필요',
  amount_unverified: '전액 환불 확인 불가·자동 회수 없음',
  payment_unrecorded: '원장 도입 전 결제 환불·관리자 수동 회수 필요',
  used_before_refund: '사용 후 환불·남은 포함 견적권 회수·확인 필요',
}

/** 확인 처리 결과. 회수는 자동 회수와 같은 '미사용 권리 회수' 로 바뀌므로, 누가 했는지는 이 줄로 구분한다 */
const REVIEW: Record<NonNullable<AdminPaymentLedgerView['review']>['decision'], string> = {
  revoke: '관리자 수동 회수',
  dismiss: '관리자 확인 완료(회수 안 함)',
}

export function PaymentLedgerPanel({
  userId,
  onEntitlements,
}: {
  userId: string
  /** 회수로 잔액이 바뀌면 사용자 카드의 잔액도 바꾼다 */
  onEntitlements: (entitlements: Entitlements) => void
}) {
  const getToken = useGetToken()
  const [items, setItems] = useState<AdminPaymentLedgerView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reload, setReload] = useState(0)
  const [reviewing, setReviewing] = useState<AdminPaymentLedgerView | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const page = usePagedList(items, 10)
  useEffect(() => {
    let current = true
    setItems(null)
    setError(null)
    request<{ items: AdminPaymentLedgerView[] }>(
      getToken,
      `/admin/users/${encodeURIComponent(userId)}/payments`,
    )
      .then((result) => {
        if (current) setItems(result.items)
      })
      .catch((cause: unknown) => {
        if (current) setError(toMessage(cause))
      })
    return () => {
      current = false
    }
  }, [getToken, userId, reload])
  return (
    <section className="flex flex-col gap-3" aria-label="결제·환불 기록">
      <div className="flex items-center justify-between gap-3">
        <Text variant="heading">결제·환불 기록</Text>
        <Button variant="ghost" size="sm" onClick={() => setReload((value) => value + 1)}>
          새로고침
        </Button>
      </div>
      <Text variant="secondary" size="xs">
        최근 50개 이벤트예요. 미사용 권리만 회수하며 사용분·부분 환불·확인 불가 건은 운영자가
        확인해요. 원장 도입 전 결제와 스토어의 전체 결제 내역을 대신하지 않아요. 확인 필요 행은
        [처리]에서 안 쓴 지급분을 회수하거나 회수하지 않고 확인 완료로 닫아요.
      </Text>
      {notice && <Banner variant="default" title={notice} />}
      {error ? (
        <Banner variant="error" title="기록을 불러오지 못했어요" description={error} />
      ) : items === null ? (
        <Loader />
      ) : items.length === 0 ? (
        <Text variant="secondary" size="sm">
          기록된 결제·환불 이벤트가 없어요.
        </Text>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>시각·상품</Table.Head>
                  <Table.Head>상태</Table.Head>
                  <Table.Head>결제 / 환불</Table.Head>
                  <Table.Head>권리 처리</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {page.slice.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <div>{fmtTime(item.createdAt)}</div>
                      <div className="text-xs">
                        {item.productId ?? '상품 미확인'} · {item.store ?? '스토어 미확인'}
                        {item.isSandbox ? ' · 테스트' : ''}
                      </div>
                      <details className="max-w-72 text-xs">
                        <summary className="cursor-pointer">거래 식별자</summary>
                        <div className="break-all">이벤트: {item.eventId}</div>
                        <div className="break-all">
                          스토어 거래: {item.storeTransactionId ?? '없음'}
                        </div>
                        <div className="break-all">
                          지급 거래: {item.grantTransactionId ?? '없음'}
                        </div>
                        <div>{item.eventType}</div>
                      </details>
                    </Table.Cell>
                    <Table.Cell>{STATUS[item.status]}</Table.Cell>
                    <Table.Cell>
                      {item.amount ?? '—'} / {item.refundAmount ?? '—'}{' '}
                      {item.currency ?? '통화 미확인'}
                    </Table.Cell>
                    <Table.Cell>
                      {item.grantAction ? ACTION[item.grantAction] : '—'}
                      {item.needsReview && (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge variant="warning">확인 필요</Badge>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setNotice(null)
                              setReviewing(item)
                            }}
                          >
                            처리
                          </Button>
                        </div>
                      )}
                      {item.review && (
                        <div className="mt-1 max-w-72 text-xs">
                          {REVIEW[item.review.decision]} · {item.review.adminEmail} ·{' '}
                          {fmtTime(item.review.at)}
                          <div className="break-all">사유: {item.review.reason}</div>
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <TablePagination
            page={page.page}
            setPage={page.setPage}
            perPage={page.perPage}
            total={page.total}
          />
        </>
      )}
      <PaymentReviewDialog
        userId={userId}
        item={reviewing}
        actionLabel={reviewing?.grantAction ? ACTION[reviewing.grantAction] : '권리 처리 미확인'}
        onDone={(message, entitlements) => {
          setReviewing(null)
          setNotice(message)
          onEntitlements(entitlements)
          setReload((value) => value + 1)
        }}
        onClose={() => setReviewing(null)}
      />
    </section>
  )
}
